import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber } from '../../styles/tokens'
import StatusBadge from '../../components/StatusBadge'
import { ErrorBanner, SkeletonRows } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

// P1: builds the same 12-field shape SummaryTable already expects, but
// entirely from the immutable freeze_snapshot -- never from the live
// estimate_revision_calculation_chain View. Every operation below is
// addition/subtraction over values freeze_estimate_revision already
// wrote verbatim at freeze time; no financial formula is recomputed
// (no markup %, no rate, no re-derivation of tender_price_total).
//
// Numeric normalization is intentionally strict: a present-but-non-numeric
// financial value throws, rather than silently becoming 0 (which could
// mask malformed snapshot data as a clean zero). Absent keys (fields that
// didn't exist yet in older snapshots) are treated as 0, per the existing
// documented convention in IndirectBudgetReviewPage.jsx.
function toFinancialNumber(value, fieldPath) {
  if (value === undefined || value === null) return 0
  const n = Number(value)
  if (!Number.isFinite(n)) {
    throw new Error(`Frozen snapshot contains a non-numeric financial value at ${fieldPath}: ${JSON.stringify(value)}`)
  }
  return n
}

function buildFrozenChain(snap) {
  const boqData = Array.isArray(snap.full_boq_data) ? snap.full_boq_data : []
  const indirect = snap.full_indirect_data && typeof snap.full_indirect_data === 'object' ? snap.full_indirect_data : {}
  // package_specific_costs is jsonb null in most historical snapshots
  // (confirmed: 10 of 12 current Frozen revisions) -- that represents
  // "no package-specific costs existed", equivalent to an empty set.
  const pkgCosts = Array.isArray(indirect.package_specific_costs) ? indirect.package_specific_costs : []

  let boqSiteOverhead = 0, boqRisk = 0, boqItemMarkup = 0
  boqData.forEach((item, i) => {
    boqSiteOverhead += toFinancialNumber(item.boq_specific_site_overhead, `full_boq_data[${i}].boq_specific_site_overhead`)
    boqRisk += toFinancialNumber(item.boq_specific_risk, `full_boq_data[${i}].boq_specific_risk`)
    boqItemMarkup += toFinancialNumber(item.selling_price, `full_boq_data[${i}].selling_price`) - toFinancialNumber(item.cost_base, `full_boq_data[${i}].cost_base`)
  })

  let pkgSiteOverhead = 0, pkgRisk = 0, pkgMarkup = 0
  pkgCosts.forEach((pkg, i) => {
    pkgSiteOverhead += toFinancialNumber(pkg.package_specific_site_overhead, `package_specific_costs[${i}].package_specific_site_overhead`)
    pkgRisk += toFinancialNumber(pkg.package_specific_risk, `package_specific_costs[${i}].package_specific_risk`)
    pkgMarkup += toFinancialNumber(pkg.package_specific_cost_with_markup, `package_specific_costs[${i}].package_specific_cost_with_markup`)
      - toFinancialNumber(pkg.package_specific_cost, `package_specific_costs[${i}].package_specific_cost`)
  })

  return {
    direct_cost_total: toFinancialNumber(snap.direct_cost_total, 'direct_cost_total'),
    boq_specific_site_overhead_total: boqSiteOverhead,
    boq_specific_risk_total: boqRisk,
    package_specific_site_overhead_total: pkgSiteOverhead,
    package_specific_risk_total: pkgRisk,
    package_specific_markup_total: pkgMarkup,
    general_site_overhead_total: toFinancialNumber(indirect.general_site_overhead_total, 'full_indirect_data.general_site_overhead_total'),
    ho_overhead_total: toFinancialNumber(snap.ho_overhead_total, 'ho_overhead_total'),
    general_contingency_total: toFinancialNumber(indirect.general_contingency_total, 'full_indirect_data.general_contingency_total'),
    boq_item_markup_total: boqItemMarkup,
    markup_total: toFinancialNumber(snap.markup_total, 'markup_total'),
    tender_price_total: toFinancialNumber(snap.tender_price_total, 'tender_price_total'),
  }
}

export default function TenderSummaryPage() {
  const { tenderId, revisionId } = useParams()
  const [chain, setChain] = useState(null)
  const [revision, setRevision] = useState(null)
  const [tax, setTax] = useState(undefined)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      const [revRes, taxRes] = await Promise.all([
        supabase.from('estimate_revision').select('label, status').eq('id', revisionId).single(),
        supabase.from('tax_setting').select('*').eq('estimate_revision_id', revisionId).maybeSingle(),
      ])
      if (cancelled) return
      if (revRes.error) return setError(revRes.error.message)
      setRevision(revRes.data)
      setTax(taxRes.data)

      // P1: same proven Draft/Frozen split already used by
      // DirectBudgetReviewPage.jsx / IndirectBudgetReviewPage.jsx --
      // status decides the source; Frozen never reads the live
      // estimate_revision_calculation_chain View.
      if (revRes.data?.status === 'frozen') {
        const snapRes = await supabase
          .from('freeze_snapshot')
          .select('direct_cost_total, ho_overhead_total, markup_total, tender_price_total, full_boq_data, full_indirect_data')
          .eq('estimate_revision_id', revisionId)
          .maybeSingle()
        if (cancelled) return
        if (snapRes.error) return setError(snapRes.error.message)
        if (!snapRes.data) return setError('This revision is marked frozen but no snapshot record could be found.')

        try {
          setChain(buildFrozenChain(snapRes.data))
        } catch (e) {
          setError(e.message)
        }
        return
      }

      const chainRes = await supabase
        .from('estimate_revision_calculation_chain')
        .select('*')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      if (cancelled) return
      if (chainRes.error) return setError(chainRes.error.message)
      setChain(chainRes.data)
    }

    load()
    return () => { cancelled = true }
  }, [revisionId])

  if (error) return <ErrorBanner message={error} />
  if (!revision) return <SkeletonRows rows={4} cols={2} />

  return (
    <div>
      <EstimationBreadcrumb />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '10px 0 4px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Tender Summary</h1>
        <StatusBadge status={revision.status} />
      </div>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 24px' }}>{revision.label}</p>

      {!chain && (
        <div style={{ padding: 24, background: colors.warningBg, color: colors.warningText, borderRadius: 8, fontSize: 14 }}>
          No calculation chain data yet — add BOQ items with priced Rate Build-Up lines first.
        </div>
      )}

      {chain && (
        <div style={{ maxWidth: 640 }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
            <SummaryRow label="Direct Cost" value={chain.direct_cost_total} />
            <SectionLabel>BOQ-Specific Overheads</SectionLabel>
            <SummaryRow label="BOQ-Specific Site Overhead" value={chain.boq_specific_site_overhead_total} indent />
            <SummaryRow label="BOQ-Specific Risk" value={chain.boq_specific_risk_total} indent />
            <SectionLabel>Package-Specific Costs</SectionLabel>
            <SummaryRow label="Package Site Overhead" value={chain.package_specific_site_overhead_total} indent />
            <SummaryRow label="Package Risk" value={chain.package_specific_risk_total} indent />
            <SummaryRow label="Package Markup" value={chain.package_specific_markup_total} indent />
            <SectionLabel>Revision-General (No Markup)</SectionLabel>
            <SummaryRow label="General Site Overhead" value={chain.general_site_overhead_total} indent />
            <SummaryRow label="HO Overhead" value={chain.ho_overhead_total} indent />
            <SummaryRow label="General Contingency" value={chain.general_contingency_total} indent />
            <SectionLabel>Markup</SectionLabel>
            <SummaryRow label="BOQ Item Markup" value={chain.boq_item_markup_total} indent />
            <SummaryRow label="Total Markup" value={chain.markup_total} indent bold />
            <SummaryRow label="TENDER PRICE" value={chain.tender_price_total} final />
            {tax && (
              <>
                <SummaryRow label={`${tax.tax_label} (${formatNumber(tax.tax_pct)}%)`} value={Number(chain.tender_price_total) * Number(tax.tax_pct) / 100} indent />
                <SummaryRow
                  label={`TENDER PRICE incl. ${tax.tax_label}`}
                  value={Number(chain.tender_price_total) * (1 + Number(tax.tax_pct) / 100)}
                  final
                />
              </>
            )}
          </div>

          {tax && (
            <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
              {tax.tax_label} is calculated for display here (tender_price_total × {tax.tax_label} %) — it is not part of{' '}
              <code>estimate_revision_calculation_chain</code> and does not feed back into any other calculation.
            </p>
          )}

          <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 12 }}>
            {revision.status === 'frozen'
              ? <>All figures are read directly from the immutable <code>freeze_snapshot</code> taken when this revision was frozen — no calculation is performed in this application.</>
              : <>All figures are read directly from <code>estimate_revision_calculation_chain</code> — no calculation is performed in this application.</>}
          </p>
        </div>
      )}
    </div>
  )
}

function SectionLabel({ children }) {
  return (
    <div style={{ padding: '10px 20px 4px', fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, background: '#FAFBFC' }}>
      {children}
    </div>
  )
}

function SummaryRow({ label, value, indent, bold, final }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: final ? '16px 20px' : '9px 20px',
        paddingLeft: indent ? 36 : 20,
        borderTop: final ? `2px solid ${colors.borderStrong}` : 'none',
        background: final ? '#F8FAFF' : 'transparent',
      }}
    >
      <span style={{ fontSize: final ? 15 : 14, fontWeight: final || bold ? 700 : 400, color: final ? colors.primary : colors.textPrimary }}>
        {label}
      </span>
      <span style={{ fontSize: final ? 18 : 14, fontWeight: final || bold ? 700 : 400, color: final ? colors.primary : colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(value)}
      </span>
    </div>
  )
}
